import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Loader2, Sparkles } from 'lucide-react';
import { solveXiaoHua, type XiaoHuaSolveResponse } from '../api/xiaohua';
import { XiaoHuaViewer } from '../components/xiaohua/XiaoHuaViewer';

export default function XiaoHuaPage() {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<XiaoHuaSolveResponse | null>(null);

  const handleSolve = async () => {
    if (!content.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await solveXiaoHua({ content: content.trim() });
      setResult(res);
    } catch (e: any) {
      setError(e.message || '出错了，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSolve();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      <div className="container mx-auto py-6 px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-3xl"> </span>
            <h1 className="text-2xl font-bold text-slate-800">小画老师</h1>
          </div>
          <p className="text-sm text-slate-500">
            用画图的方式讲解数学题，让 1-4 年级的小朋友轻松理解！
          </p>
        </div>

        {/* Input area */}
        {!result && (
          <Card className="mb-6 border-amber-200 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-amber-800">输入题目</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                placeholder="请输入小学应用题，例如：&#10;&#10;小明有 12 个苹果，小红比小明多 5 个，小红有多少个苹果？"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={4}
                className="text-base resize-none"
                disabled={loading}
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">
                  Ctrl + Enter 快速提交
                </span>
                <Button
                  onClick={handleSolve}
                  disabled={loading || !content.trim()}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      小画老师在思考...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-1" />
                      让小画老师来讲
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error */}
        {error && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="py-3 text-sm text-red-700">
              {error}
              <Button
                variant="link"
                className="text-red-700 ml-2 p-0 h-auto"
                onClick={() => { setError(''); setResult(null); }}
              >
                重试
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Result viewer */}
        {result && (
          <>
            <Button
              variant="outline"
              size="sm"
              className="mb-4"
              onClick={() => { setResult(null); setError(''); }}
            >
              输入新题目
            </Button>
            <XiaoHuaViewer
              steps={result.steps}
              finalAnswer={result.final_answer}
              summaryMarkdown={result.summary_markdown}
              problemStem={result.problem_stem}
            />
          </>
        )}
      </div>
    </div>
  );
}
